import { useEffect, useRef, useState } from "react";

/**
 * Hook for creating a typing animation effect that streams text character by character
 * @param text - The full text to animate
 * @param speed - Speed of typing in milliseconds per character
 * @param delay - Initial delay before starting the animation
 * @param isActive - Whether the animation should be active
 * @param onComplete - Callback when animation completes
 * @returns The current displayed text and animation state
 */
export const useTypingAnimation = (
  text: string,
  speed: number = 10,
  delay: number = 0,
  isActive: boolean = true,
  onComplete?: () => void,
) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const hasCalledOnComplete = useRef(false);

  useEffect(() => {
    if (!isActive || !text) {
      setDisplayedText("");
      setIsComplete(false);
      hasCalledOnComplete.current = false;
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const startAnimation = () => {
      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex < text.length) {
          setDisplayedText(text.slice(0, currentIndex + 1));
          currentIndex++;
          timeoutId = setTimeout(typeNextChar, speed);
        } else {
          setIsComplete(true);
          if (!hasCalledOnComplete.current && onComplete) {
            hasCalledOnComplete.current = true;
            onComplete();
          }
        }
      };

      timeoutId = setTimeout(typeNextChar, speed);
    };

    const initialTimeoutId = setTimeout(startAnimation, delay);

    return () => {
      clearTimeout(initialTimeoutId);
      clearTimeout(timeoutId);
    };
  }, [text, speed, delay, isActive, onComplete]);

  return { displayedText, isComplete };
};
